import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JobDetailDto } from "@/lib/jobs";
import { jobsApi, isJobContentLocked } from "@/lib/jobs";
import { FieldGridCell } from "@/components/field-grid-cell";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BomPreviewTable } from "@/components/bom/BomPreviewTable";
import { ProductPicker } from "@/components/products/ProductPicker";
import { MfgBuilderPanel } from "@/components/mfg/builder/MfgBuilderPanel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Pencil } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { CreateTaskDialog } from "@/components/tasks/CreateTaskDialog";
import { usePermissions } from "@/hooks/use-permissions";
import { JobTasksList } from "./JobTasksList";
import { formatDate, formatDateTime } from "@/lib/format";

const fmt = new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" });

export interface JobDetailsTabProps {
  job: JobDetailDto;
  /** Optional callback fired when user clicks "+ Add Item" on the BOM preview.
   *  When omitted, the tab manages picker state locally (Task 8 default).
   *  Lift to JobDetailPage only if the picker must cross tab boundaries. */
  onAddBomItem?: () => void;
}

/**
 * Details tab for the Job Detail page — matches `Job Details.png`.
 *
 * Six panels:
 *   1. Job Status (status-code checkboxes, one-click coarse transitions)
 *   2. Current Tasks (Create / Reissue buttons stubbed; list from approvals
 *      data which is currently not on the JobDetailDto — renders empty)
 *   3. Core Details (FieldList — owner, branch, customer ref, PO, etc.)
 *   4. Job Details RTE (`job.description` rendered as plain prose for now)
 *   5. Special Notes RTE (placeholder — Notes API fetch lives in NotesTab
 *      pre-restructure; Plan II keeps a placeholder here)
 *   6. BOM preview (mini-table; empty until the BOM-by-parent endpoint ships)
 *
 * Wiring stubs marked TODO inline and listed in the Plan II runbook.
 * Create/Reissue Task buttons stay disabled until the follow-up task ships
 * the matching mutations.
 */
export function JobDetailsTab({
  job,
  onAddBomItem,
}: JobDetailsTabProps) {
  // Task 8: local picker state — flipped on by the BOM "+ Add Item" button.
  // When the parent provides `onAddBomItem`, defer to it (lifted state pattern).
  const [pickerOpen, setPickerOpen] = useState(false);
  const handleAddBomItem = onAddBomItem ?? (() => setPickerOpen(true));

  // Task 12: manufacturing item builder in a Dialog, gated on mfg-bom:write
  // AND job content not being locked (same editability guard as job details).
  const [mfgOpen, setMfgOpen] = useState(false);

  // Plan 2 T-10: Create / Reissue Task wiring. Both open a job-seeded
  // CreateTaskDialog. Gated on tasks:create:production (hidden when absent).
  const { has } = usePermissions();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const canCreateTask = has("tasks:create:production");
  const canAddMfg = has("mfg-bom:write") && !isJobContentLocked(job.currentStatusCode);

  if (pickerOpen) {
    return (
      <ProductPicker
        context={{ kind: "job", id: job.jobId }}
        onCancel={() => setPickerOpen(false)}
        onAdded={() => setPickerOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* 3. Core Details — 2 cols on small, 4 cols on md+ (8 items total) */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Core Details
        </h3>
        <dl className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <FieldGridCell label="Owner" value={job.ownerEmployeeName ?? "—"} />
          <FieldGridCell
            label="Branch"
            value={job.branchId != null ? `Branch #${job.branchId}` : "—"}
          />
          <FieldGridCell label="Customer Ref" value={job.customerReference ?? "—"} />
          <FieldGridCell label="PO" value={job.purchaseOrderNumber ?? "—"} />
          <FieldGridCell
            label="Expected Dispatch"
            value={
              job.expectedDispatchDate
                ? formatDate(job.expectedDispatchDate)
                : "—"
            }
          />
          <FieldGridCell
            label="Total ex GST"
            value={job.totalExGst != null ? fmt.format(job.totalExGst) : "—"}
          />
          <FieldGridCell
            label="Total inc GST"
            value={job.totalIncGst != null ? fmt.format(job.totalIncGst) : "—"}
          />
          <FieldGridCell
            label="Status timestamp"
            value={formatDateTime(job.currentStatusEnteredUtc)}
          />
        </dl>
      </section>

      {/* 2. Current Tasks */}
      <section className="rounded-lg border border-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Current Tasks
          </h3>
          {canCreateTask && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="h-7 rounded-full px-3 text-xs"
                onClick={() => setCreateOpen(true)}
              >
                Create Task
              </Button>
              {/* "Reissue" currently behaves as a job-seeded create; clone-of-completed is a future enhancement. */}
              <Button
                variant="outline"
                className="h-7 rounded-full px-3 text-xs"
                onClick={() => setCreateOpen(true)}
              >
                Reissue Task
              </Button>
            </div>
          )}
        </div>
        <JobTasksList jobId={job.jobId} />
        {canCreateTask && (
          <CreateTaskDialog
            open={createOpen}
            onOpenChange={setCreateOpen}
            presetType="Production"
            presetJobId={job.jobId}
            presetJobLabel={job.jobReferenceNo}
            onCreated={() =>
              void queryClient.invalidateQueries({
                queryKey: ["task-list", "job", job.jobId],
              })
            }
          />
        )}
      </section>

      {/* 4. Job Details — view/edit (textarea) */}
      <JobDetailsDescriptionSection job={job} locked={isJobContentLocked(job.currentStatusCode)} />

      {/* 5. Special Notes — view/edit (textarea) backed by JobNotes */}
      <JobDetailsSpecialNotesSection jobId={job.jobId} statusCode={job.currentStatusCode} />

      {/* 6. BOM preview */}
      {/* BomPreviewTable renders its own "BOM preview" header + "+ Add Item";
          the mfg-tree add sits as a sibling control, gated on mfg-bom:write. */}
      {canAddMfg && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={() => setMfgOpen(true)}>
            + Manufacturing item
          </Button>
        </div>
      )}
      <BomPreviewTable jobId={job.jobId} onAddItem={handleAddBomItem} />

      {/* Task 12: MfgBuilderPanel in a Dialog — overlays the tab; gated on mfg-bom:write. */}
      <Dialog open={mfgOpen} onOpenChange={setMfgOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add a manufacturing item</DialogTitle>
            <DialogDescription className="sr-only">Configure a tree BOM and price it.</DialogDescription>
          </DialogHeader>
          <MfgBuilderPanel
            context={{ kind: "job", id: job.jobId }}
            onCancel={() => setMfgOpen(false)}
            onAdded={() => setMfgOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// §4 subcomponent — Job Details description (view/edit pair)
// ---------------------------------------------------------------------------


function JobDetailsDescriptionSection({ job, locked }: { job: JobDetailDto; locked: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(job.description ?? "");

  // Reset draft when the underlying job description changes (e.g. after refetch),
  // but only when not in edit mode — otherwise an in-flight user edit gets clobbered.
  useEffect(() => {
    if (!editing) setDraft(job.description ?? "");
  }, [job.description, editing]);

  // Two gates (both surfaced via editDisabledReason): status must not be locked
  // AND rowVersion must be present.
  const editDisabledReason = locked
    ? `This job is ${job.currentStatusCode} and is read-only.`
    : job.rowVersionBase64 == null
      ? "Concurrency token missing — refresh the page to enable editing."
      : null;

  const saveMut = useMutation({
    mutationFn: () =>
      jobsApi.update(job.jobId, {
        clientId: job.clientId,
        addressId: job.addressId,
        companyId: job.companyId,
        branchId: job.branchId,
        ownerEmployeeId: job.ownerEmployeeId,
        description: draft || null,
        customerReference: job.customerReference,
        purchaseOrderNumber: job.purchaseOrderNumber,
        expectedDispatchDate: job.expectedDispatchDate,
        dueDate: null,
        rowVersionBase64: job.rowVersionBase64!,
      }),
    onSuccess: () => {
      toast.success("Job details saved");
      void qc.invalidateQueries({ queryKey: ["job", job.jobId] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  return (
    <section className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Job Details
        </h3>
        {!editing && (
          editDisabledReason == null ? (
            <Button
              data-testid="edit-job-details"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-slate-400 hover:text-slate-600"
              onClick={() => setEditing(true)}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span role="button" aria-disabled="true" tabIndex={0}>
                    <Button
                      data-testid="edit-job-details"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-slate-600"
                      disabled
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{editDisabledReason}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            data-testid="textarea-job-details"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={8}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(job.description ?? "");
                setEditing(false);
              }}
              disabled={saveMut.isPending}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-job-details"
              size="sm"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm whitespace-pre-wrap">
          {job.description ? (
            job.description
          ) : (
            <span className="text-muted-foreground">No description</span>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// §5 subcomponent — Special Notes (view/edit pair, backed by JobNotes)
// ---------------------------------------------------------------------------

function JobDetailsSpecialNotesSection({ jobId, statusCode }: { jobId: number; statusCode: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const locked = isJobContentLocked(statusCode);

  const notesQuery = useQuery({
    queryKey: ["job", jobId, "notes"],
    queryFn: () => jobsApi.getNotes(jobId),
  });

  // Same gating as §4 — don't clobber an in-flight edit when the query refetches.
  useEffect(() => {
    if (!editing) setDraft(notesQuery.data?.note ?? "");
  }, [notesQuery.data?.note, editing]);

  // Two gates: status must not be locked AND if notes exist, rowVersion must be present.
  const editDisabledReason = locked
    ? `This job is ${statusCode} and is read-only.`
    : (notesQuery.data?.note && notesQuery.data?.rowVersionBase64 == null) && !notesQuery.isLoading
      ? "Concurrency token missing — refresh the page to enable editing."
      : null;

  const saveMut = useMutation({
    mutationFn: () => jobsApi.putNotes(jobId, {
      note: draft,
      rowVersionBase64: notesQuery.data?.rowVersionBase64 ?? "",
    }),
    onSuccess: () => {
      toast.success("Special notes saved");
      void qc.invalidateQueries({ queryKey: ["job", jobId, "notes"] });
      setEditing(false);
    },
    onError: (err: Error) => toast.error(`Save failed: ${err.message}`),
  });

  return (
    <section className="rounded-lg border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Special Notes
        </h3>
        {!editing && (
          editDisabledReason == null || notesQuery.isLoading ? (
            <Button
              data-testid="edit-special-notes"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-slate-400 hover:text-slate-600"
              disabled={notesQuery.isLoading}
              onClick={() => setEditing(true)}
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span role="button" aria-disabled="true" tabIndex={0}>
                    <Button
                      data-testid="edit-special-notes"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-slate-400 hover:text-slate-600"
                      disabled
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>{editDisabledReason}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <Textarea
            data-testid="textarea-special-notes"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={6}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setDraft(notesQuery.data?.note ?? ""); setEditing(false); }}
              disabled={saveMut.isPending}
            >
              Cancel
            </Button>
            <Button
              data-testid="save-special-notes"
              size="sm"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm whitespace-pre-wrap">
          {notesQuery.isLoading
            ? <span className="text-muted-foreground">Loading…</span>
            : notesQuery.data?.note
              ? notesQuery.data.note
              : <span className="text-muted-foreground">No notes</span>}
        </div>
      )}
    </section>
  );
}
